import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://public.readdy.ai/ai/img_res/40beb105-aa73-4e53-a2f4-5cf10142dfbd.png";

const footerLinks = [
  {
    title: "Services",
    links: ["AI Lead Capture", "CRM Automation", "Underwriting Systems", "Compliance Reporting"],
  },
  {
    title: "Industries",
    links: ["Financial Advisory", "Mortgage & Lending", "Fintech Platforms", "Real Estate"],
  },
  {
    title: "Company",
    links: ["About", "Process", "Results", "Contact"],
  },
];

export default function Footer() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll<HTMLElement>("[data-reveal]");
            els.forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = "1";
                el.style.transform = "translateY(0)";
              }, i * 80);
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <footer className="bg-bfx-graphite text-white relative overflow-hidden" ref={sectionRef}>
      <div className="absolute inset-0 grid-bg-dark opacity-30 pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-bfx-accent/20 to-transparent" />

      {/* CTA block */}
      <div id="cta" className="relative z-10 border-b border-white/[0.07]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 text-center">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-5"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Get Started
          </p>
          <h2
            data-reveal
            className="text-4xl md:text-5xl lg:text-[58px] font-semibold text-white leading-[1.05] tracking-tight mb-5"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Build Your Intelligence System.
          </h2>
          <p
            data-reveal
            className="text-base text-white/50 max-w-sm mx-auto leading-relaxed mb-10"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Replace fragmented workflows with a unified intelligence layer.
          </p>
          <div
            data-reveal
            className="flex flex-col sm:flex-row items-center justify-center gap-3"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            <button className="whitespace-nowrap px-8 py-3.5 rounded-full bg-bfx-accent text-white text-sm font-semibold hover:bg-[#2878e8] transition-all duration-200 cursor-pointer">
              Schedule Strategy Call
            </button>
            <button className="whitespace-nowrap px-8 py-3.5 rounded-full border border-white/15 text-white text-sm font-medium hover:bg-white/8 transition-all duration-200 cursor-pointer">
              Learn More
              <i className="ri-arrow-right-line ml-2 text-xs" />
            </button>
          </div>
        </div>
      </div>

      {/* Footer nav */}
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-16 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-14">
          {/* Brand */}
          <div className="md:col-span-1">
            <Link to="/" className="flex items-center gap-2.5 mb-5 cursor-pointer">
              <img src={LOGO_URL} alt="BlueForceX AI" className="w-7 h-7 object-contain" />
              <span className="text-sm font-semibold tracking-tight">
                BlueForce<span className="text-bfx-accent">X</span>
              </span>
            </Link>
            <p className="text-sm text-white/40 leading-relaxed">
              Precision intelligence infrastructure for financial and real estate operators.
            </p>
          </div>

          {footerLinks.map((group) => (
            <div key={group.title}>
              <h4 className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/30 mb-4">
                {group.title}
              </h4>
              <ul className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <li key={link}>
                    <a href="#" className="text-sm text-white/50 hover:text-white transition-colors cursor-pointer">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/[0.07] pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-xs text-white/25">
            &copy; {new Date().getFullYear()} BlueForceX AI. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-white/25 hover:text-white/50 transition-colors cursor-pointer">Privacy Policy</a>
            <a href="#" className="text-xs text-white/25 hover:text-white/50 transition-colors cursor-pointer">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
}